/**
 * `W4-T09` (E8) — the jobs a professional has won, shared by `/work` and `/work/:jobId`.
 *
 * **Read off the professional's own quotes**, because there is no other door: `GET /api/jobs/:id` is
 * the owner's and the feed lists `OPEN` jobs only. A quote reads `AWARDED` once the call-out fee has
 * settled (`W4-T05`), and the handshake leaves it so, which makes `status === 'AWARDED'` exactly a job
 * that is `AWARDED` or `IN_PROGRESS` — nothing earlier, nothing lost.
 *
 * Fetched fresh (`staleTime: 0`): an award arrives on the server while the professional is elsewhere.
 */
import { type Quote } from '@marketplace/contracts';
import { type RouterContextProvider } from 'react-router';
import { ApiError } from '../../shared/api.js';
import { queryKeys, routeContext } from '../../shared/query.js';

export async function loadAwardedQuotes(
  context: Readonly<RouterContextProvider>,
): Promise<Quote[]> {
  const { queryClient, api } = context.get(routeContext);
  try {
    const quotes = await queryClient.fetchQuery({
      queryKey: queryKeys.myQuotes(),
      queryFn: () => api.getMyQuotes(),
      staleTime: 0,
    });
    return quotes.filter((quote) => quote.status === 'AWARDED');
  } catch (error) {
    // A client-only account has no quotes to read (`quote:read-own` is `PROVIDER`'s): no work, which
    // is the truth, rather than an error page in front of an empty list.
    if (error instanceof ApiError && error.status === 403) return [];
    throw error;
  }
}
